/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { classNamesFunction, FontWeights } from '@fluentui/react';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';

export type AutotagTriggerModalReviewAndFinishProps = {
    autotagInfoDto: AutoLabelingInfoDto;
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
};

const messages = defineMessages({
    jobType: { id: 'customText.ReviewAndFinishScreen.jobType', defaultMessage: 'Job type' },
    title: { id: 'customText.reviewAndFinishScreen.title', defaultMessage: 'Review and finish' },
    files: { id: 'customText.ReviewAndFinishScreen.files', defaultMessage: 'Files to be auto-labeled' },
    trainedModel: { id: 'customText.ReviewAndFinishScreen.trainedModel', defaultMessage: 'Trained model' },
    gptJobType: { id: 'customText.ReviewAndFinishScreen.gptJobType', defaultMessage: 'Auto-label with GPT' },
    classes: { id: 'customText.ReviewAndFinishScreen.classes', defaultMessage: 'Existing Classes to be labeled' },
    entities: { id: 'customText.ReviewAndFinishScreen.entities', defaultMessage: 'Existing entities to be labeled' },
    characters: { id: 'customText.ReviewAndFinishScreen.characters', defaultMessage: 'Number of text records consumed' },
    trainedModelJobType: {
        id: 'customText.ReviewAndFinishScreen.trainedModelJobType',
        defaultMessage: `Auto-label based on a model you've trained`
    },
    description: {
        id: 'customText.reviewAndFinishScreen.description',
        defaultMessage: 'Review the configurations you set for your auto-labeling job in the previous steps.'
    }
});

const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { minHeight: '500px', height: '100%', position: 'relative' }
});

export const ReviewAndFinishScreen = observer((props: AutotagTriggerModalReviewAndFinishProps) => {
    const { autotagInfoDto, onSubmit, onDismiss, onPrevious } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const isGptJob = autotagInfoDto.autoLabelingJobType === 'gpt';
    const { projectKind } = rootStore.currentCustomTextProjectStore;
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const trainedModelData = (
        <Stack tokens={{ childrenGap: 6 }}>
            <LuisText variant="medium" fontWeight={FontWeights.semibold as Property.FontWeight}>
                {localize(messages.trainedModel)}
            </LuisText>
            <LuisText styles={{ root: { paddingLeft: '5px' } }}>{autotagInfoDto.trainedModelLabel}</LuisText>
        </Stack>
    );

    return (
        <Stack>
            <ScrollablePane styles={ScrollablePaneStyles}>
                <Stack
                    styles={{ root: { height: '100%', overflowY: 'auto' } }}
                    tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(messages.title)}
                        </LuisText>
                        <LuisText>{localize(messages.description)}</LuisText>
                    </Stack>
                    {FeatureToggles.CustomTextGptLabeling
                        ? [
                              <Stack tokens={{ childrenGap: 6 }}>
                                  <LuisText variant="medium" fontWeight={FontWeights.semibold as Property.FontWeight}>
                                      {localize(messages.jobType)}
                                  </LuisText>
                                  <LuisText styles={{ root: { paddingLeft: '5px' } }}>
                                      {localize(isGptJob ? messages.gptJobType : messages.trainedModelJobType)}
                                  </LuisText>
                              </Stack>,
                              ...(isGptJob ? [] : [trainedModelData])
                          ]
                        : trainedModelData}
                    {!isGptJob && (
                        <Stack tokens={{ childrenGap: 6 }}>
                            <LuisText variant="medium" fontWeight={FontWeights.semibold as Property.FontWeight}>
                                {localize(messages.characters)}
                            </LuisText>
                            <LuisText styles={{ root: { paddingLeft: '5px' } }}>{autotagInfoDto.selectedChars}</LuisText>
                        </Stack>
                    )}
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText variant="medium" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(isExtractionProject ? messages.entities : messages.classes)}
                        </LuisText>
                        <LuisText styles={{ root: { paddingLeft: '5px' } }}>
                            {autotagInfoDto[isExtractionProject ? 'entitiesNames' : 'classesNames'].join(', ')}
                        </LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 6 }}>
                        <LuisText variant="medium" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(messages.files)}
                        </LuisText>
                        <LuisText styles={{ root: { paddingLeft: '5px' } }}>
                            {autotagInfoDto.documents.map(doc => doc.name).join(', ')}
                        </LuisText>
                    </Stack>
                </Stack>
            </ScrollablePane>
            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                onNext={() => onSubmit()}
                activeStep={AutoLabelingSteps.ReviewAndFinish}
            />
        </Stack>
    );
});
